import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { concatLatestFrom } from "@ngrx/operators";
import { Store } from "@ngrx/store";
import { JsonConvert } from "json2typescript";
import { of } from "rxjs";
import {
    catchError,
    filter,
    map,
    switchMap,
    tap,
    withLatestFrom,
} from "rxjs/operators";

import {
    AddToCartResponse,
    Basket,
    CustomOptions,
    ErrorItem,
    PersistResponse,
    RemoveFromCartResponse,
    SetCartItemResponse,
} from "@hermes/api-model-basket";
import { CartService } from "@hermes/hermes-web-fragments-cart-service";
import { EcomErrorCodes } from "@hermes/utils-generic-constants";

import * as basketActions from "../actions/basket.actions";
import { EcomError, SerializedBasket } from "../models/basket-feature.model";
import { State } from "../reducers/basket.reducer";
import { basketQuery } from "../selectors/basket.selectors";
import { BasketService } from "../services/basket.service";

@Injectable()
export class BasketEffects {
    readonly #actions$ = inject(Actions);
    readonly #cartService = inject(CartService);
    readonly #store = inject<Store<State>>(Store);
    readonly #basketService = inject(BasketService);

    public addToCart$ = createEffect(() =>
        this.#actions$.pipe(
            ofType(basketActions.addToCart),
            switchMap(({ addToCartPayload }) =>
                this.#cartService
                    .addProductToCart(
                        addToCartPayload.sku,
                        addToCartPayload.skuSet,
                        addToCartPayload.dku,
                        addToCartPayload.urlImage,
                        addToCartPayload.customOptions,
                    )
                    .pipe(
                        map((response: AddToCartResponse) => {
                            // if ecp respond with basket object : add to cart is OK
                            if (response.basket && !response.error) {
                                return basketActions.addToCartSuccess({
                                    basket: this.#deserializeBasket(
                                        response.basket,
                                    ),
                                    displayNotifOnSuccess:
                                        addToCartPayload.displayNotifOnSuccess,
                                    itemPosition: addToCartPayload.itemPosition,
                                    sku: addToCartPayload.sku,
                                    templateType: addToCartPayload.templateType,
                                    sendAnalyticsEvent:
                                        addToCartPayload.sendAnalyticsEvent,
                                    isProductEngraving: Boolean(
                                        addToCartPayload.customOptions
                                            ?.engraving?.length,
                                    ),
                                    context: addToCartPayload.context,
                                    product: addToCartPayload.product,
                                    currencyCode: addToCartPayload.currencyCode,
                                });
                            }

                            // In some case, ecp can respond OK 200 with a technical error stack, so create an unknown error
                            return basketActions.addToCartFailure({
                                error: response.error || {
                                    internal_code: EcomErrorCodes.UNKNOWN_ERROR,
                                },
                                itemPosition: addToCartPayload.itemPosition,
                                shouldOpenModal: Boolean(
                                    addToCartPayload.displayNotifOnFail,
                                ),
                                context: addToCartPayload.context,
                                quickBuyRevamp: addToCartPayload.quickBuyRevamp,
                            });
                        }),
                        catchError((error) =>
                            of(
                                basketActions.addToCartFailure({
                                    error: {
                                        internal_code:
                                            this.mapAddToCartError(error),
                                    },
                                    itemPosition: addToCartPayload.itemPosition,
                                    shouldOpenModal: Boolean(
                                        addToCartPayload.displayNotifOnFail,
                                    ),
                                    context: addToCartPayload.context,
                                    quickBuyRevamp:
                                        addToCartPayload.quickBuyRevamp,
                                }),
                            ),
                        ),
                    ),
            ),
        ),
    );

    public addManyToCart$ = createEffect(() =>
        this.#actions$.pipe(
            ofType(basketActions.addManyToCart),
            switchMap(({ addManyToCartPayload }) =>
                this.#cartService
                    .addManyProductsToCart(
                        addManyToCartPayload.items.map((item) => item.sku),
                    )
                    .pipe(
                        map((response: AddToCartResponse) => {
                            if (response.basket && !response.error) {
                                return basketActions.addManyToCartSuccess({
                                    basket: this.#deserializeBasket(
                                        response.basket,
                                    ),

                                    itemPosition:
                                        addManyToCartPayload.itemPosition,

                                    displayNotifOnSuccess:
                                        addManyToCartPayload.displayNotifOnSuccess,

                                    context: addManyToCartPayload.context,
                                    skus: addManyToCartPayload.items.map(
                                        (item) => item.sku,
                                    ),
                                    templateType:
                                        addManyToCartPayload.templateType,
                                });
                            }

                            return basketActions.addManyToCartFailure({
                                error: response.error ?? {
                                    internal_code: EcomErrorCodes.UNKNOWN_ERROR,
                                },

                                itemPosition: addManyToCartPayload.itemPosition,

                                shouldOpenModal: Boolean(
                                    addManyToCartPayload.displayNotifOnFail,
                                ),

                                context: addManyToCartPayload.context,

                                quickBuyRevamp:
                                    addManyToCartPayload.quickBuyRevamp,
                            });
                        }),

                        catchError((error) =>
                            of(
                                basketActions.addManyToCartFailure({
                                    error: {
                                        internal_code:
                                            this.mapAddToCartError(error),
                                    },

                                    itemPosition:
                                        addManyToCartPayload.itemPosition,

                                    shouldOpenModal: Boolean(
                                        addManyToCartPayload.displayNotifOnFail,
                                    ),

                                    context: addManyToCartPayload.context,

                                    quickBuyRevamp:
                                        addManyToCartPayload.quickBuyRevamp,
                                }),
                            ),
                        ),
                    ),
            ),
        ),
    );

    public removeFromCart$ = createEffect(() =>
        this.#actions$.pipe(
            ofType(basketActions.removeFromCart),
            concatLatestFrom(() => this.#store.select(basketQuery.selectCart)),
            switchMap(([payload, cart]) => {
                const item = cart?.items?.find(
                    (cartItem) => cartItem.sku === payload.sku,
                );
                const removedItem = item
                    ? { sku: item.sku, name: item.name, price: item.unitPrice }
                    : undefined;
                return this.#cartService.removeProductsFromCart([payload]).pipe(
                    map((response: RemoveFromCartResponse) =>
                        basketActions.removeFromCartSuccess({
                            basket: this.#deserializeBasket(response.basket),
                            removedItem,
                            deliveryBlockerContext:
                                response.deliveryBlockerContext,
                            deliveryError: response.deliveryError,
                        }),
                    ),
                    catchError(() =>
                        // when response is not OK 200, create error with ecom technical error code
                        of(
                            basketActions.removeFromCartFailure({
                                error: {
                                    internal_code:
                                        EcomErrorCodes.TECHNICAL_ERROR,
                                },
                            }),
                        ),
                    ),
                );
            }),
        ),
    );

    public resetCart$ = createEffect(() =>
        this.#actions$.pipe(
            ofType(basketActions.resetCart),
            withLatestFrom(this.#store.select(basketQuery.selectCart)),
            switchMap(([_, basket]) =>
                this.#cartService
                    .removeProductsFromCart(
                        basket.items.map((item) => {
                            const typedItem = item as {
                                sku: string;
                                qty: number;
                                customOptions?: CustomOptions;
                            };
                            return {
                                sku: typedItem.sku,
                                qty: typedItem.qty,
                                customOptions: typedItem.customOptions,
                            };
                        }),
                    )
                    .pipe(
                        map((response: RemoveFromCartResponse) =>
                            basketActions.resetCartSuccess({
                                basket: this.#deserializeBasket(
                                    response.basket,
                                ),
                            }),
                        ),
                        catchError(() =>
                            // when response is not OK 200, create error with ecom technical error code
                            of(
                                basketActions.resetCartFailure({
                                    error: {
                                        internal_code:
                                            EcomErrorCodes.TECHNICAL_ERROR,
                                    },
                                }),
                            ),
                        ),
                    ),
            ),
        ),
    );

    public persistBasket$ = createEffect(() =>
        this.#actions$.pipe(
            ofType(basketActions.persistBasket),
            switchMap((payload) =>
                this.#basketService.persist(payload.persistRequest).pipe(
                    tap((response: PersistResponse) => {
                        if (response.error) {
                            throw new Error(response.error);
                        }
                    }),
                    filter(
                        (response: PersistResponse) =>
                            response.basket && !response.error,
                    ),
                    map((response: PersistResponse) =>
                        basketActions.persistBasketSuccess({
                            basket: this.#deserializeBasket(response.basket),
                        }),
                    ),
                    // when response is not OK 200, create error with ecom technical error code
                    catchError(() =>
                        of(
                            basketActions.persistBasketFailure({
                                error: {
                                    internal_code:
                                        EcomErrorCodes.TECHNICAL_ERROR,
                                },
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    public setCartItem$ = createEffect(() =>
        this.#actions$.pipe(
            ofType(basketActions.setCartItem),
            concatLatestFrom(() => this.#store.select(basketQuery.selectCart)),
            switchMap(([payload, cart]) => {
                const previousItem = cart?.items?.find(
                    (cartItem) => cartItem.sku === payload.sku,
                );
                const previousQty = previousItem?.qty ?? 0;
                const removedItem = previousItem
                    ? {
                          sku: previousItem.sku,
                          name: previousItem.name,
                          price: previousItem.unitPrice,
                      }
                    : undefined;
                return this.#cartService
                    .setCartItemProduct(
                        payload.sku,
                        payload.qty,
                        payload.customOptions,
                    )
                    .pipe(
                        map((response: SetCartItemResponse) => {
                            // If ecp respond with basket object : remove from cart is OK
                            if (response.basket && !response.error) {
                                return basketActions.setCartItemSuccess({
                                    basket: this.#deserializeBasket(
                                        response.basket,
                                    ),
                                    sku: payload.sku,
                                    qty: payload.qty,
                                    previousQty,
                                    deliveryBlockerContext:
                                        response.deliveryBlockerContext,
                                    deliveryError: response.error,
                                });
                            }

                            // In some case, ecp can respond OK 200 with a technical error stack, so create an unknown error
                            const error: EcomError = {
                                internal_code: EcomErrorCodes.UNKNOWN_ERROR,
                            };

                            if (response.error) {
                                const errorItem =
                                    new JsonConvert().deserializeObject(
                                        response.error,
                                        ErrorItem,
                                    );
                                error.internal_code =
                                    errorItem.internalCode as EcomErrorCodes;
                                error["sku"] = errorItem.item;
                                if (
                                    error.internal_code ===
                                    EcomErrorCodes.DELETED_ITEM
                                ) {
                                    return basketActions.removeFromCartSuccess({
                                        basket: response.basket,
                                        removedItem,
                                        deliveryBlockerContext:
                                            response.deliveryBlockerContext,
                                        deliveryError: response.error,
                                    });
                                }
                            }

                            return basketActions.setCartItemFailure({
                                error,
                            });
                        }),
                        // when response is not OK 200, create error with ecom technical error code
                        catchError(() =>
                            of(
                                basketActions.setCartItemFailure({
                                    error: {
                                        internal_code:
                                            EcomErrorCodes.TECHNICAL_ERROR,
                                    },
                                }),
                            ),
                        ),
                    );
            }),
        ),
    );

    #deserializeBasket(basket: Basket | SerializedBasket): Basket {
        if (!("total_items" in basket)) {
            return basket;
        }

        return new JsonConvert().deserializeObject(basket, Basket);
    }

    private mapAddToCartError(error: {
        error?: { error?: string; errors?: Array<{ type?: string }> };
    }): EcomErrorCodes {
        switch (error?.error?.error) {
            case "AvailabilitiesError": {
                return EcomErrorCodes.INSUFFICIENT_STOCK;
            }
            case "MaxChildQuantityError": {
                return EcomErrorCodes.MAX_CHILD_QUANTITY;
            }
            case "ProductNotFeasibleError": {
                return EcomErrorCodes.ERROR_CODE_PERSONALIZATION_CAPACITY;
            }
            case "PersonalizeItemError": {
                return EcomErrorCodes.ERROR_CODE_PERSONALIZATION_MIXEDITEMS;
            }
            case "PlatformError": {
                return EcomErrorCodes.ERROR_CODE_PERSONALIZATION_PLATFORME;
            }
            case "OutOfStockError": {
                return EcomErrorCodes.OUT_OF_STOCK;
            }
            default: {
                return EcomErrorCodes.TECHNICAL_ERROR;
            }
        }
    }
}
